import {
  DataMessageTypeEnum,
  type TerraUIMessage,
  TerraUIMessageDataPartName,
} from '@terra/shared-dto-chat-types';
import { getUserMessageDisplayText } from '../../utils/userMessageDisplayText';
import {
  sanitizeCoPilotExportCsvCell,
  sanitizeCoPilotExportMarkdownPlainText,
  sanitizeCoPilotExportRequestSlug,
} from './co-pilot-export-sanitize';

export const CO_PILOT_TRANSCRIPT_ASSISTANT_LABEL = 'Terra Co-pilot';
export const CO_PILOT_TRANSCRIPT_EXPORT_SOURCE = 'Terra Co-pilot';

const OBJECT_CONTEXT_TITLE_PREFIX =
  /^(Finding|Signal|EndPoint|WebPage|Trace|Skill) · /;

export type CoPilotTranscriptTurn =
  | { speaker: 'user'; text: string }
  | { speaker: 'assistant'; text: string };

type CoPilotTranscriptMarkdownParams = {
  title: string | null | undefined;
  threadId: string;
  exportedAt: string;
  turns: CoPilotTranscriptTurn[];
  timeZone?: string;
  assistantLabel?: string;
};

export function sanitizeTranscriptTitle(
  title: string | null | undefined,
): string | null {
  const trimmed = title?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

export function buildCoPilotTranscriptFilename(
  title: string | null | undefined,
): string {
  const sanitized = sanitizeTranscriptTitle(title);
  const slug = sanitized
    ? sanitizeCoPilotExportRequestSlug(sanitized)
    : 'co_pilot_chat';

  return `${slug}.md`;
}

export function buildCoPilotSessionExportFilename({
  title,
  extension,
}: {
  title: string | null | undefined;
  extension: string;
}): string {
  const normalizedExtension = extension.replace(/^\./, '');
  return buildCoPilotTranscriptFilename(title).replace(
    /\.md$/,
    `.${normalizedExtension}`,
  );
}

export function buildCoPilotSessionHarExportFilename({
  title,
}: {
  title: string | null | undefined;
}): string {
  return buildCoPilotSessionExportFilename({ title, extension: 'csv' }).replace(
    /\.csv$/,
    '_har.csv',
  );
}

const TRANSCRIPT_CSV_USER_SPEAKER_LABEL = 'User';

export function buildCoPilotTranscriptCsv({
  title,
  threadId,
  messages,
  exportedAt = new Date().toISOString(),
  timeZone,
  assistantLabel = CO_PILOT_TRANSCRIPT_ASSISTANT_LABEL,
}: {
  title: string | null | undefined;
  threadId: string;
  messages: TerraUIMessage[];
  exportedAt?: string;
  timeZone?: string;
  assistantLabel?: string;
}): string {
  const displayTitle = sanitizeCoPilotExportMarkdownPlainText(
    sanitizeTranscriptTitle(title) ?? 'Co-pilot chat',
  );
  const exportTime = formatCoPilotTranscriptExportDateTime({
    exportedAt,
    timeZone,
  });
  const turns = buildCoPilotTranscriptTurns(messages);
  const metadataLines = [
    ['Title', displayTitle],
    ['Export time', exportTime],
    ['Thread Id', threadId],
  ].map(([label, value]) =>
    [label, value].map((cell) => sanitizeCoPilotExportCsvCell(cell)).join(','),
  );

  const turnLines = [
    ['speaker', 'text'].join(','),
    ...turns.map((turn) => {
      const speaker =
        turn.speaker === 'user'
          ? TRANSCRIPT_CSV_USER_SPEAKER_LABEL
          : assistantLabel;
      return [speaker, turn.text]
        .map((cell) => sanitizeCoPilotExportCsvCell(cell))
        .join(',');
    }),
  ];

  return [...metadataLines, '', ...turnLines].join('\n');
}

export function countCoPilotTranscriptCsvRows(csv: string): number {
  const lines = csv.trim().split('\n');
  const speakerHeaderIndex = lines.findIndex((line) =>
    line.startsWith('speaker,'),
  );
  if (speakerHeaderIndex < 0) {
    return 0;
  }
  return Math.max(0, lines.length - speakerHeaderIndex - 1);
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

export function resolveTranscriptTimeZone(timeZone?: string): string {
  const trimmed = timeZone?.trim();
  if (!trimmed) {
    return 'UTC';
  }

  if (!isValidIanaTimeZone(trimmed)) {
    return 'UTC';
  }

  return trimmed;
}

function readDateTimePart({
  date,
  timeZone,
  type,
}: {
  date: Date;
  timeZone: string;
  type: Intl.DateTimeFormatPartTypes;
}): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone,
  }).formatToParts(date);

  return parts.find((part) => part.type === type)?.value ?? '';
}

function formatGmtOffsetLabel({
  date,
  timeZone,
}: {
  date: Date;
  timeZone: string;
}): string {
  try {
    const timeZoneName = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;

    const offsetMatch = /GMT([+-]\d{1,2})(?::00)?/.exec(timeZoneName ?? '');
    if (offsetMatch) {
      return `GMT${offsetMatch[1]}`;
    }

    return timeZoneName ?? 'GMT';
  } catch {
    return 'GMT';
  }
}

export function formatCoPilotTranscriptExportDateTime({
  exportedAt,
  timeZone,
}: {
  exportedAt: string;
  timeZone?: string;
}): string {
  const resolvedTimeZone = resolveTranscriptTimeZone(timeZone);
  const date = new Date(exportedAt);

  if (Number.isNaN(date.getTime())) {
    return exportedAt;
  }

  const month = readDateTimePart({
    date,
    timeZone: resolvedTimeZone,
    type: 'month',
  });
  const day = readDateTimePart({
    date,
    timeZone: resolvedTimeZone,
    type: 'day',
  });
  const year = readDateTimePart({
    date,
    timeZone: resolvedTimeZone,
    type: 'year',
  });
  const hour = readDateTimePart({
    date,
    timeZone: resolvedTimeZone,
    type: 'hour',
  });
  const minute = readDateTimePart({
    date,
    timeZone: resolvedTimeZone,
    type: 'minute',
  });
  const second = readDateTimePart({
    date,
    timeZone: resolvedTimeZone,
    type: 'second',
  });
  const offsetLabel = formatGmtOffsetLabel({
    date,
    timeZone: resolvedTimeZone,
  });

  return `${month}/${day}/${year} at ${hour}:${minute}:${second} ${offsetLabel}`;
}

export function formatCoPilotTranscriptHeader({
  title,
  threadId,
  exportedAt,
  timeZone,
}: {
  title: string | null | undefined;
  threadId: string;
  exportedAt: string;
  timeZone?: string;
}): string {
  const displayTitle = sanitizeCoPilotExportMarkdownPlainText(
    sanitizeTranscriptTitle(title) ?? 'Co-pilot chat',
  );
  const exportedAtLabel = formatCoPilotTranscriptExportDateTime({
    exportedAt,
    timeZone,
  });

  return `${displayTitle}\n\nExported from ${CO_PILOT_TRANSCRIPT_EXPORT_SOURCE} · ${exportedAtLabel}\n\nThread ID: ${sanitizeCoPilotExportMarkdownPlainText(threadId)}`;
}

function resolveTitleFromFirstUserObjectContextMention(
  messages: TerraUIMessage[],
): string | null {
  const firstUserMessage = messages.find((message) => message.role === 'user');
  if (!firstUserMessage) {
    return null;
  }

  for (const part of firstUserMessage.parts ?? []) {
    if (
      part.type !== TerraUIMessageDataPartName ||
      part.data?.type !== DataMessageTypeEnum.Mentions
    ) {
      continue;
    }

    const objectContextMention = part.data.mentions.find(
      (mention) => mention.entityType === 'ObjectContext' && mention.label,
    );
    if (objectContextMention?.label) {
      return `@${objectContextMention.label}`;
    }
  }

  const displayText = getUserMessageDisplayText({ message: firstUserMessage });
  const mentionMatch = displayText.match(
    /^@(Finding|Signal|EndPoint|WebPage|Trace|Skill) · [^\n]+/,
  );
  return mentionMatch?.[0]?.trim() ?? null;
}

export function resolveCoPilotTranscriptDisplayTitle({
  title,
  messages,
  linkedObject,
}: {
  title?: string | null;
  messages?: TerraUIMessage[];
  linkedObject?: { kind: string; label?: string };
}): string | null {
  if (linkedObject?.kind && linkedObject.label?.trim()) {
    return `@${linkedObject.kind} · ${linkedObject.label.trim()}`;
  }

  const sanitizedTitle = sanitizeTranscriptTitle(title);
  if (sanitizedTitle?.startsWith('@')) {
    return sanitizedTitle;
  }
  if (sanitizedTitle && OBJECT_CONTEXT_TITLE_PREFIX.test(sanitizedTitle)) {
    return `@${sanitizedTitle}`;
  }

  const titleFromMention =
    messages && messages.length > 0
      ? resolveTitleFromFirstUserObjectContextMention(messages)
      : null;
  if (titleFromMention) {
    return titleFromMention;
  }

  return sanitizedTitle;
}

function readAssistantTextFromMessage(message: TerraUIMessage): string {
  return message.parts
    .filter(
      (part): part is { type: 'text'; text: string } =>
        part.type === 'text' && typeof part.text === 'string',
    )
    .map((part) => part.text)
    .join('')
    .trim();
}

export function buildCoPilotTranscriptTurns(
  messages: TerraUIMessage[],
): CoPilotTranscriptTurn[] {
  const turns: CoPilotTranscriptTurn[] = [];

  for (const message of messages) {
    if (message.role === 'user') {
      const text = sanitizeCoPilotExportMarkdownPlainText(
        getUserMessageDisplayText({ message }).trim(),
      );
      if (text) {
        turns.push({ speaker: 'user', text });
      }
      continue;
    }

    if (message.role !== 'assistant') {
      continue;
    }

    const text = sanitizeCoPilotExportMarkdownPlainText(
      readAssistantTextFromMessage(message),
    );
    if (text) {
      turns.push({ speaker: 'assistant', text });
    }
  }

  return turns;
}

export function mergeConsecutiveCoPilotTranscriptTurns(
  turns: CoPilotTranscriptTurn[],
): CoPilotTranscriptTurn[] {
  const merged: CoPilotTranscriptTurn[] = [];

  for (const turn of turns) {
    const previousTurn = merged[merged.length - 1];
    if (previousTurn && previousTurn.speaker === turn.speaker) {
      merged[merged.length - 1] = {
        speaker: turn.speaker,
        text: `${previousTurn.text}\n\n${turn.text}`,
      };
      continue;
    }

    merged.push({ ...turn });
  }

  return merged;
}

function formatCoPilotTranscriptTurnBlock({
  turn,
  assistantLabel,
}: {
  turn: CoPilotTranscriptTurn;
  assistantLabel: string;
}): string {
  const speakerHeading =
    turn.speaker === 'user' ? '## User' : `## ${assistantLabel}`;

  return `${speakerHeading}\n\n${turn.text}`;
}

export function formatCoPilotTranscriptMarkdown({
  title,
  threadId,
  exportedAt,
  turns,
  timeZone,
  assistantLabel = CO_PILOT_TRANSCRIPT_ASSISTANT_LABEL,
}: CoPilotTranscriptMarkdownParams): string {
  const header = formatCoPilotTranscriptHeader({
    title,
    threadId,
    exportedAt,
    timeZone,
  });
  const turnBlocks = turns.map((turn) =>
    formatCoPilotTranscriptTurnBlock({ turn, assistantLabel }),
  );

  if (turnBlocks.length === 0) {
    return `${header}\n`;
  }

  return `${header}\n\n${turnBlocks.join('\n\n')}\n`;
}

export function buildCoPilotTranscriptExport({
  title,
  threadId,
  messages,
  exportedAt = new Date().toISOString(),
  timeZone,
  assistantLabel,
  linkedObject,
}: {
  title: string | null | undefined;
  threadId: string;
  messages: TerraUIMessage[];
  exportedAt?: string;
  timeZone?: string;
  assistantLabel?: string;
  linkedObject?: { kind: string; label?: string };
}): { markdown: string; filename: string } {
  const displayTitle = resolveCoPilotTranscriptDisplayTitle({
    title,
    messages,
    linkedObject,
  });
  const turns = buildCoPilotTranscriptTurns(messages);

  return {
    markdown: formatCoPilotTranscriptMarkdown({
      title: displayTitle,
      threadId,
      exportedAt,
      turns,
      timeZone,
      assistantLabel,
    }),
    filename: buildCoPilotTranscriptFilename(displayTitle),
  };
}
